import type {
  PlatformAccess,
  PlatformStats,
  PlatformUser,
  ProvisionTenantRequest,
  ProvisionTenantResult,
  SetTenantTierRequest,
  SubscriptionTier,
  TenantDetail,
  TenantSummary,
  TierKey,
} from '@worklogix/shared';
import { ApiError, createApiClient, useAuth } from '@worklogix/web-common';
import type { ApiClient } from '@worklogix/web-common';
import { useMemo } from 'react';

// Re-export so console components can branch on transport failures (e.g. the
// 403 gate probe, or a 409 slug conflict on provision) without reaching into
// the web-common client module directly.
export { ApiError };

/**
 * Typed facade over the platform-admin control-plane API (`/platform/*`).
 * Every method is a thin, strongly-typed wrapper over the shared
 * {@link ApiClient}; shapes are the shared DTOs, so the API contract is the
 * single source of truth.
 */
export interface PlatformApi {
  /** `GET /platform/access` — 200 grants the console, 403 (thrown) denies it. */
  getAccess: () => Promise<PlatformAccess>;
  /** `GET /platform/stats` — dashboard KPI aggregates. */
  getStats: () => Promise<PlatformStats>;
  /** `GET /platform/tenants` — resource-list of all tenants. */
  listTenants: () => Promise<TenantSummary[]>;
  /** `GET /platform/tenants/:id` — a single tenant's detail. */
  getTenant: (id: string) => Promise<TenantDetail>;
  /** `GET /platform/tenants/:id/users` — users scoped to one tenant. */
  listTenantUsers: (id: string) => Promise<PlatformUser[]>;
  /**
   * `POST /platform/tenants` — idempotent provision; `created=false` on slug
   * reuse. Accepts an optional `subscriptionTier` on the request (defaults to
   * the free tier server-side when omitted).
   */
  provisionTenant: (input: ProvisionTenantRequest) => Promise<ProvisionTenantResult>;
  /** `GET /platform/tiers` — the global subscription-tier catalog. */
  getTiers: () => Promise<SubscriptionTier[]>;
  /**
   * `PATCH /platform/tenants/:id/tier` — set a tenant's subscription tier.
   * Soft-warn only: a downgrade below current usage succeeds (the tenant is
   * then surfaced as "over limit"); the API never blocks the change.
   */
  setTenantTier: (id: string, tier: TierKey) => Promise<TenantDetail>;
}

/**
 * Build a {@link PlatformApi} over an existing {@link ApiClient}. Pure and
 * React-free so it can be constructed in tests with a stub client.
 */
export function createPlatformApi(client: ApiClient): PlatformApi {
  return {
    getAccess: () => client.request<PlatformAccess>('/platform/access'),
    getStats: () => client.request<PlatformStats>('/platform/stats'),
    listTenants: () => client.request<TenantSummary[]>('/platform/tenants'),
    getTenant: (id) => client.request<TenantDetail>(`/platform/tenants/${encodeURIComponent(id)}`),
    listTenantUsers: (id) =>
      client.request<PlatformUser[]>(`/platform/tenants/${encodeURIComponent(id)}/users`),
    provisionTenant: (input) =>
      client.request<ProvisionTenantResult>('/platform/tenants', {
        method: 'POST',
        body: input,
      }),
    getTiers: () => client.request<SubscriptionTier[]>('/platform/tiers'),
    setTenantTier: (id, tier) =>
      client.request<TenantDetail>(`/platform/tenants/${encodeURIComponent(id)}/tier`, {
        method: 'PATCH',
        body: { subscriptionTier: tier } satisfies SetTenantTierRequest,
      }),
  };
}

/**
 * React hook exposing the platform API bound to the signed-in user's token and
 * the `VITE_API_BASE` origin. Memoized on the token acquirer so consumers get a
 * stable reference across renders.
 */
export function usePlatformApi(): PlatformApi {
  const { acquireToken } = useAuth();

  return useMemo(() => {
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE,
      getToken: acquireToken,
    });
    return createPlatformApi(client);
  }, [acquireToken]);
}
